import React from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Box,
    Button,
    ButtonBase,
    Chip,
    CircularProgress,
    Stack,
    Tooltip,
    Typography,
    alpha,
    useTheme,
} from '@mui/material';
import { Iconify } from 'src/components';
import ConsultationRecordingCard from './ConsultationRecordingCard';
import { formatElapsed, statusConfig } from './consultationRecordingsUtils';
import useConsultationRecordings from './useConsultationRecordings';

const recordingMeta = (recording) => {
    const utteranceCount = Array.isArray(recording?.transcript_utterances)
        ? recording.transcript_utterances.length
        : 0;
    const parts = [
        recording?.duration_ms ? formatElapsed(recording.duration_ms) : null,
        utteranceCount ? `${utteranceCount} transcript turns` : null,
        recording?.lead_note ? 'Note saved' : null,
    ].filter(Boolean);
    return parts.join(' | ');
};

function RecorderBar({
    canManage,
    elapsedMs,
    hasRecorder,
    latestRecording,
    recordingState,
    startRecording,
    stopRecording,
}) {
    const theme = useTheme();
    const latestCfg = statusConfig(latestRecording?.status);
    const isRecording = recordingState === 'recording';
    const isBusy = ['processing', 'stopping'].includes(recordingState);

    return (
        <Box
            sx={{
                position: { md: 'sticky' },
                top: 0,
                zIndex: 1,
                p: { xs: 2, md: 2.5 },
                mb: 2,
                borderRadius: 1,
                bgcolor: 'common.white',
                border: `1px solid ${alpha(theme.palette.grey[500], 0.16)}`,
                boxShadow: `0 10px 30px ${alpha(theme.palette.grey[500], 0.08)}`,
            }}
        >
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                alignItems={{ xs: 'stretch', sm: 'center' }}
            >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                        <Box
                            sx={{
                                width: 34,
                                height: 34,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: isRecording
                                    ? alpha(theme.palette.error.main, 0.1)
                                    : alpha(theme.palette.primary.main, 0.08),
                            }}
                        >
                            <Iconify
                                icon={isRecording ? 'solar:record-bold' : 'solar:microphone-2-bold'}
                                width={18}
                                height={18}
                                sx={{ color: isRecording ? 'error.main' : 'primary.main' }}
                            />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                Consultation recordings
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
                                Capture the clinic conversation, then review transcript and save the note.
                            </Typography>
                        </Box>
                    </Stack>
                </Box>

                {latestRecording && !isRecording && (
                    <Chip
                        size="small"
                        color={latestCfg.color}
                        icon={<Iconify icon={latestCfg.icon} width={14} height={14} />}
                        label={`Latest ${latestCfg.label}`}
                        sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
                    />
                )}

                {isRecording ? (
                    <Button
                        color="error"
                        variant="contained"
                        onClick={stopRecording}
                        startIcon={<Iconify icon="solar:stop-circle-bold" />}
                        sx={{ minWidth: 150 }}
                    >
                        Stop {formatElapsed(elapsedMs)}
                    </Button>
                ) : (
                    <Tooltip title={!hasRecorder ? 'This browser does not support recording' : ''}>
                        <span>
                            <Button
                                variant="contained"
                                disabled={!canManage || !hasRecorder || recordingState !== 'idle'}
                                onClick={startRecording}
                                startIcon={isBusy ? <CircularProgress size={16} color="inherit" /> : <Iconify icon="solar:microphone-2-bold" />}
                                sx={{ minWidth: 150 }}
                            >
                                {isBusy ? 'Preparing' : 'Record'}
                            </Button>
                        </span>
                    </Tooltip>
                )}
            </Stack>
        </Box>
    );
}

RecorderBar.propTypes = {
    canManage: PropTypes.bool.isRequired,
    elapsedMs: PropTypes.number.isRequired,
    hasRecorder: PropTypes.bool.isRequired,
    latestRecording: PropTypes.object,
    recordingState: PropTypes.string.isRequired,
    startRecording: PropTypes.func.isRequired,
    stopRecording: PropTypes.func.isRequired,
};

RecorderBar.defaultProps = {
    latestRecording: null,
};

function RecordingList({ recordings, selectedId, onSelect }) {
    const theme = useTheme();

    if (recordings.length === 0) {
        return (
            <Box
                sx={{
                    py: 5,
                    px: 2,
                    textAlign: 'center',
                    color: 'text.secondary',
                    border: `1px dashed ${alpha(theme.palette.grey[500], 0.24)}`,
                    borderRadius: 1,
                    bgcolor: alpha(theme.palette.grey[500], 0.04),
                }}
            >
                <Iconify icon="solar:microphone-2-bold" width={30} height={30} />
                <Typography variant="subtitle2" sx={{ mt: 1, color: 'text.primary' }}>
                    No recordings yet
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Start a recording when the patient is in clinic.
                </Typography>
            </Box>
        );
    }

    return (
        <Stack spacing={1} sx={{ maxHeight: { md: 640 }, overflow: 'auto', pr: { md: 0.5 } }}>
            {recordings.map((recording) => {
                const cfg = statusConfig(recording?.status);
                const selected = recording.id === selectedId;
                const cfgColor = theme.palette[cfg.color]?.main || theme.palette.grey[500];
                return (
                    <ButtonBase
                        key={recording.id}
                        onClick={() => onSelect(recording.id)}
                        sx={{
                            width: '100%',
                            display: 'block',
                            textAlign: 'left',
                            p: 1.25,
                            borderRadius: 1,
                            border: `1px solid ${selected ? theme.palette.primary.main : alpha(theme.palette.grey[500], 0.16)}`,
                            bgcolor: selected ? alpha(theme.palette.primary.main, 0.06) : 'common.white',
                            transition: theme.transitions.create(['border-color', 'background-color']),
                            '&:hover': {
                                borderColor: selected ? theme.palette.primary.main : alpha(theme.palette.grey[500], 0.32),
                                bgcolor: selected ? alpha(theme.palette.primary.main, 0.08) : alpha(theme.palette.grey[500], 0.04),
                            },
                        }}
                    >
                        <Stack direction="row" spacing={1.25} alignItems="flex-start">
                            <Box
                                sx={{
                                    width: 34,
                                    height: 34,
                                    flexShrink: 0,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: alpha(cfgColor, 0.1),
                                }}
                            >
                                <Iconify
                                    icon={cfg.icon}
                                    width={17}
                                    height={17}
                                    sx={{ color: cfgColor }}
                                />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
                                    <Typography variant="subtitle2" noWrap sx={{ flex: 1 }}>
                                        {recording?.created_at_human || recording?.started_at || 'Consultation'}
                                    </Typography>
                                    <Chip size="small" color={cfg.color} label={cfg.label} sx={{ height: 22 }} />
                                </Stack>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                                    {recordingMeta(recording) || 'Recording captured'}
                                </Typography>
                            </Box>
                        </Stack>
                    </ButtonBase>
                );
            })}
        </Stack>
    );
}

RecordingList.propTypes = {
    recordings: PropTypes.array.isRequired,
    selectedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onSelect: PropTypes.func.isRequired,
};

RecordingList.defaultProps = {
    selectedId: null,
};

export default function ConsultationRecordings({ leadDetail, compact }) {
    const {
        canView,
        canManage,
        elapsedMs,
        error,
        hasRecorder,
        loading,
        recordings,
        recordingState,
        approveNote,
        pollRecording,
        startRecording,
        stopRecording,
        updateSpeakerMap,
    } = useConsultationRecordings(leadDetail);
    const [selectedRecordingId, setSelectedRecordingId] = React.useState(null);
    const selectedRecording = React.useMemo(
        () => recordings.find((recording) => recording.id === selectedRecordingId) || recordings[0] || null,
        [recordings, selectedRecordingId]
    );

    React.useEffect(() => {
        if (!recordings.length) {
            setSelectedRecordingId(null);
            return;
        }
        if (!selectedRecordingId || !recordings.some((recording) => recording.id === selectedRecordingId)) {
            setSelectedRecordingId(recordings[0].id);
        }
    }, [recordings, selectedRecordingId]);

    if (!canView) return null;

    return (
        <Box sx={{ p: compact ? 2 : 0, bgcolor: 'common.white' }}>
            <RecorderBar
                canManage={canManage}
                elapsedMs={elapsedMs}
                hasRecorder={hasRecorder}
                latestRecording={recordings[0]}
                recordingState={recordingState}
                startRecording={startRecording}
                stopRecording={stopRecording}
            />

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading ? (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                    <CircularProgress size={26} />
                </Box>
            ) : (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', lg: compact ? '1fr' : '340px minmax(0, 1fr)' },
                        gap: 2,
                        alignItems: 'start',
                    }}
                >
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <Typography variant="subtitle2" sx={{ flex: 1 }}>
                                Recordings
                            </Typography>
                            <Chip size="small" label={recordings.length} />
                        </Stack>
                        <RecordingList
                            recordings={recordings}
                            selectedId={selectedRecording?.id}
                            onSelect={setSelectedRecordingId}
                        />
                    </Box>

                    <ConsultationRecordingCard
                        recording={selectedRecording}
                        canManage={canManage}
                        onRefresh={pollRecording}
                        onApprove={approveNote}
                        onSpeakerMapChange={updateSpeakerMap}
                    />
                </Box>
            )}
        </Box>
    );
}

ConsultationRecordings.propTypes = {
    leadDetail: PropTypes.object.isRequired,
    compact: PropTypes.bool,
};

ConsultationRecordings.defaultProps = {
    compact: false,
};
